import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import moment from "moment"
import { ArrowLeft, Mail, Phone } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import NavigationLink from "~/library/NavigationLink"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceContacts } from "~/routes"
import type Contact from "~/types/Contact"
import ResourceNotes from "~/library/ResourceNotes"
import CatchDropdown from "~/library/CatchDropdown"
import { useToast } from "~/utils/Toast"

const niceDate = (d?: string | null) =>
    d ? moment(d).format("MMM Do YYYY, HH:mm") : "—"

const renderDetail = (title: string, content: React.ReactNode, size = 6) => (
    <div className={`col-${size}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
            <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
    </div>
)

const ContactView: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const toast = useToast()
    const [isDeleting, setIsDeleting] = useState(false)

    const endpoint = apiWorkspaceContacts.show.path({ venue_slug: venueSlug, id: id! })
    const { data: contact, isLoading } = useFetchResource<{ contact: Contact }, "contact">(
        endpoint,
        "contact",
        {}
    )

    const handleDelete = async () => {
        if (!confirm("Are you sure you want to delete this contact? This action cannot be undone.")) {
            return
        }

        setIsDeleting(true)

        try {
            const url = apiWorkspaceContacts.destroy.path({
                venue_slug: venueSlug,
                id: id!
            })

            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            })

            const result = await response.json()

            if (result.status === 'ok' || response.ok) {
                toast("Contact deleted successfully", "success")
                navigate("/contacts")
            } else {
                toast(
                    `Error deleting contact. ${result.errors?.join(", ") || "Please try again"}`,
                    "danger"
                )
                setIsDeleting(false)
            }
        } catch (error) {
            console.error("Error:", error)
            toast("Error deleting contact. Please try again", "danger")
            setIsDeleting(false)
        }
    }

    if (isLoading || !contact) {
        return <LoadingWrapper isLoading />
    }

    return (
        <LoadingWrapper isLoading={isDeleting}>
            <PageTitle
                title={
                    <div className="d-flex align-items-center">
                        <button
                            type="button"
                            className="btn btn-link p-0 me-2"
                            onClick={() => navigate("/contacts")}
                            aria-label="Back to Contacts"
                            title="Back to Contacts"
                        >
                            <ArrowLeft />
                        </button>
                        <span>
                            {contact.firstName} {contact.lastName}
                        </span>
                    </div>
                }
            >
                <CatchDropdown
                    label="Actions"
                    className="ms-2"
                    items={[
                        {
                            label: "Edit Contact",
                            to: `/contacts/${contact?.prefixId}/edit`,
                        },
                        {
                            label: "Delete Contact",
                            onClick: handleDelete,
                            className: "text-danger",
                        }
                    ]}
                />
            </PageTitle>

            <div className="row">
                <div className="col-8">
                    <CatchCard title="Contact Information">
                        <div className="row">
                            {renderDetail("First Name", contact.firstName || "—", 6)}
                            {renderDetail("Last Name", contact.lastName || "—", 6)}

                            {renderDetail(
                                "Email",
                                contact.email ? (
                                    <a href={`mailto:${contact.email}`} className="d-flex align-items-center gap-2">
                                        <Mail size={16} />
                                        {contact.email}
                                    </a>
                                ) : (
                                    "—"
                                ),
                                6
                            )}

                            {renderDetail(
                                "Phone",
                                contact.phone ? (
                                    <a href={`tel:${contact.phone}`} className="d-flex align-items-center gap-2">
                                        <Phone size={16} />
                                        {contact.phone}
                                    </a>
                                ) : (
                                    "—"
                                ),
                                6
                            )}

                            {renderDetail("Role", contact.role || "—", 6)}
                            {renderDetail("Category", contact.contactCategory?.name || "—", 6)}

                            {renderDetail(
                                "Account",
                                contact.account ? (
                                    <NavigationLink type="accounts" to={`/accounts/${contact.account.prefixId}`}>
                                        {contact.account.name}
                                    </NavigationLink>
                                ) : (
                                    "—"
                                ),
                                12
                            )}
                        </div>
                    </CatchCard>
                </div>

                <div className="col-4">
                    <CatchCard title="Notes">
                        <ResourceNotes resourceType="contact" resourceId={contact?.prefixId} />
                    </CatchCard>
                </div>
            </div>
        </LoadingWrapper>
    )
}

export default ContactView
